import React, { useEffect, useState, useMemo, useCallback } from 'react';
import './SapControlsRepository.css';
// Header provided by AppLayout
import Spinner from './Spinner';
import axios from 'axios';

type ControlCategory = 'authorization' | 'access' | 'segregation' | 'audit' | 'security' | 'compliance';
type ControlSeverity = 'critical' | 'high' | 'medium' | 'low';
type ComplianceFramework = 'SOX' | 'ISO27001' | 'LGPD' | 'GDPR' | 'COBIT' | 'NIST';

interface Control {
  id: string;
  name: string;
  description: string;
  category: ControlCategory;
  severity: ControlSeverity;
  frameworks: ComplianceFramework[];
  technicalDetails?: string;
  risk?: string;
}

interface ControlExecution {
  controlId: string;
  sapClientId: string;
  executedAt: string;
  executionTime: number;
  error?: string;
  result: {
    passed: boolean;
    findings: ControlFinding[];
    totalRecords: number;
    totalViolations: number;
    rawData?: any[];
    summary?: string;
    recommendations?: string[];
  };
}

interface ControlFinding {
  type: 'violation' | 'warning' | 'info';
  description: string;
  data: Record<string, any>;
  severity: ControlSeverity;
}

interface SapClient {
  _id: string;
  name: string;
  host: string;
  connectionType: string;
}

const SapControlsRepository: React.FC = () => {
  
  const [controls, setControls] = useState<Control[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ControlCategory | ''>('');
  const [severityFilter, setSeverityFilter] = useState<ControlSeverity | ''>('');
  const [frameworkFilter, setFrameworkFilter] = useState<ComplianceFramework | ''>('');
  
  // Execution modal
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [selectedControl, setSelectedControl] = useState<Control | null>(null);
  const [sapClients, setSapClients] = useState<SapClient[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState<ControlExecution | null>(null);

  // Toast notifications
  const [toast, setToast] = useState<{ 
    visible: boolean; 
    message: string; 
    variant?: 'info' | 'danger' | 'success' 
  }>({ visible: false, message: '', variant: 'info' });

  // Paginação dos achados
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // theme handled globally in AppLayout

  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base ? base.replace(/\/$/, '') : '';
  }, []);

  const loadControls = useCallback(() => {
    setLoading(true);
    const url = apiBase ? `${apiBase}/api/sap-controls` : '/api/sap-controls';
    
    const params: any = {};
    if (categoryFilter) params.category = categoryFilter;
    if (severityFilter) params.severity = severityFilter;
    if (frameworkFilter) params.framework = frameworkFilter;
    if (searchQuery) params.search = searchQuery;

    axios.get(url, { params, withCredentials: true })
      .then(res => {
        setControls(res.data.controls || []);
        setError(null);
      })
      .catch(err => {
        setError(err?.response?.data?.error || err.message || 'Erro ao carregar controles');
        setControls([]);
      })
      .finally(() => setLoading(false));
  }, [apiBase, categoryFilter, severityFilter, frameworkFilter, searchQuery]);

  const loadSapClients = useCallback(() => {
    const url = apiBase ? `${apiBase}/api/sap-clients` : '/api/sap-clients';
    axios.get(url, { withCredentials: true })
      .then(res => {
        setSapClients(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => {
        console.error('Erro ao carregar clientes SAP:', err);
      });
  }, [apiBase]);

  // Carregar dados iniciais
  useEffect(() => {
    loadControls();
    loadSapClients();
  }, [loadControls, loadSapClients]);

  // Não precisa mais deste useEffect, os filtros já estão nas dependências do loadControls

  const showToast = (message: string, variant: 'info' | 'danger' | 'success' = 'info') => {
    setToast({ visible: true, message, variant });
    setTimeout(() => setToast({ visible: false, message: '', variant: 'info' }), 4000);
  };

  const openExecutionModal = (control: Control) => {
    setSelectedControl(control);
    setExecutionResult(null);
    setSelectedClient('');
    setShowExecutionModal(true);
  };

  const executeControl = async () => {
    if (!selectedControl || !selectedClient) {
      showToast('Selecione um cliente SAP', 'danger');
      return;
    }

    setExecuting(true);
    setExecutionResult(null);
    setCurrentPage(1); // Reset paginação

    const url = apiBase 
      ? `${apiBase}/api/sap-controls/${selectedControl.id}/execute` 
      : `/api/sap-controls/${selectedControl.id}/execute`;

    try {
      const res = await axios.post(url, { sapClientId: selectedClient }, { withCredentials: true });
      setExecutionResult(res.data.execution);
      showToast('Controle executado com sucesso', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || err.message || 'Erro ao executar controle', 'danger');
    } finally {
      setExecuting(false);
    }
  };

  const getSeverityBadgeClass = (severity: ControlSeverity) => {
    const map = {
      critical: 'danger',
      high: 'warning',
      medium: 'info',
      low: 'success'
    };
    return `badge bg-${map[severity]}`;
  };

  const getSeverityLabel = (severity: ControlSeverity) => {
    const map = {
      critical: 'Crítico',
      high: 'Alto',
      medium: 'Médio',
      low: 'Baixo'
    };
    return map[severity];
  };

  const getCategoryLabel = (category: ControlCategory) => {
    const map = {
      authorization: 'Autorização',
      access: 'Acesso',
      segregation: 'Segregação de Funções',
      audit: 'Auditoria',
      security: 'Segurança',
      compliance: 'Compliance'
    };
    return map[category];
  };

  return (
    <>
      <div className="container py-5" style={{ marginTop: 80 }}>
        
        {/* Header */}
        <div className="mb-3">
          <h1 className="mb-0">Repositório de Controles SAP</h1>
          <p className="lead mb-0">Controles de auditoria e compliance para ambientes SAP</p>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 200, flex: '1 1 200px' }}>
            <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>Buscar</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Buscar controles..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyUp={e => { if (e.key === 'Enter') loadControls(); }}
              style={{ fontSize: '0.875rem' }}
            />
          </div>
          <div style={{ minWidth: 180, flex: '0 1 200px' }}>
            <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>Categoria</label>
            <select 
              className="form-select form-select-sm"
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value as ControlCategory | '')}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">Todas</option>
              <option value="authorization">Autorização</option>
              <option value="access">Acesso</option>
              <option value="segregation">Segregação de Funções</option>
              <option value="audit">Auditoria</option>
              <option value="security">Segurança</option>
              <option value="compliance">Compliance</option>
            </select>
          </div>
          <div style={{ minWidth: 150, flex: '0 1 180px' }}>
            <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>Severidade</label>
            <select 
              className="form-select form-select-sm"
              value={severityFilter}
              onChange={e => setSeverityFilter(e.target.value as ControlSeverity | '')}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">Todas</option>
              <option value="critical">Crítico</option>
              <option value="high">Alto</option>
              <option value="medium">Médio</option>
              <option value="low">Baixo</option>
            </select>
          </div>
          <div style={{ minWidth: 150, flex: '0 1 180px' }}>
            <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.875rem' }}>Framework</label>
            <select 
              className="form-select form-select-sm"
              value={frameworkFilter}
              onChange={e => setFrameworkFilter(e.target.value as ComplianceFramework | '')}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">Todos</option>
              <option value="SOX">SOX</option>
              <option value="ISO27001">ISO 27001</option>
              <option value="LGPD">LGPD</option>
              <option value="GDPR">GDPR</option>
              <option value="COBIT">COBIT</option>
              <option value="NIST">NIST</option>
            </select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="alert alert-danger d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-5">
            <Spinner />
            <p className="text-muted mt-3">Carregando controles...</p>
          </div>
        )}

        {/* Controls Grid */}
        {!loading && !error && (
          <div className="row g-4">
                    {controls.length === 0 && (
                      <div className="col-12">
                        <div className="alert alert-info">
                          <i className="bi bi-info-circle me-2"></i>
                          Nenhum controle encontrado com os filtros aplicados.
                        </div>
                      </div>
                    )}

                    {controls.map(control => (
                      <div key={control.id} className="col-12 col-sm-6 col-md-6 col-lg-4 col-xxl-3">
                        <div className="card h-100 hover-elevate-up shadow-sm">
                          <div className="card-body d-flex flex-column p-3">
                            <div className="d-flex align-items-start justify-content-between mb-2">
                              <span className="badge bg-primary" style={{ fontSize: '0.7rem' }}>{control.id}</span>
                              <span className={getSeverityBadgeClass(control.severity)} style={{ fontSize: '0.7rem' }}>
                                {getSeverityLabel(control.severity)}
                              </span>
                            </div>
                            
                            <h5 className="card-title fw-bold mb-2" style={{ fontSize: '0.95rem', lineHeight: '1.3' }}>
                              {control.name}
                            </h5>
                            <p className="card-text text-muted mb-3 flex-grow-1" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                              {control.description}
                            </p>
                            
                            <div className="mb-3">
                              <span className="badge bg-light text-dark me-1 mb-1" style={{ fontSize: '0.7rem', fontWeight: '500' }}>
                                {getCategoryLabel(control.category)}
                              </span>
                              {control.frameworks.map(fw => (
                                <span key={fw} className="badge bg-secondary me-1 mb-1" style={{ fontSize: '0.7rem' }}>
                                  {fw}
                                </span>
                              ))}
                            </div>
                            
                            <button
                              className="btn btn-sm btn-primary w-100"
                              onClick={() => openExecutionModal(control)}
                              style={{ fontSize: '0.8rem', padding: '0.375rem 0.75rem' }}
                            >
                              <i className="bi bi-play-circle me-1"></i>
                              Executar Controle
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
          </div>
        )}

        {/* Execution Modal */}
        {showExecutionModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog" style={{ marginTop: '2rem', marginBottom: '2rem', maxWidth: '95%' }}>
            <div className="modal-content" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
              <div className="modal-header">
                <h5 className="modal-title fw-bold" style={{ fontSize: '1.1rem' }}>
                  Executar Controle: {selectedControl?.name}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowExecutionModal(false)}
                  disabled={executing}
                ></button>
              </div>
              
              <div className="modal-body" style={{ maxHeight: 'calc(100vh - 12rem)', overflowY: 'auto' }}>
                {/* Control Info */}
                <div className="mb-3 pb-3 border-bottom" style={{ fontSize: '0.875rem' }}>
                  <div className="mb-2">
                    <strong>ID:</strong> {selectedControl?.id}
                  </div>
                  <div className="mb-2">
                    <strong>Descrição:</strong> {selectedControl?.description}
                  </div>
                  <div className="mb-2">
                    <strong>Categoria:</strong> {selectedControl && getCategoryLabel(selectedControl.category)}
                  </div>
                  <div className="mb-2">
                    <strong>Severidade:</strong>{' '}
                    {selectedControl && (
                      <span className={getSeverityBadgeClass(selectedControl.severity)} style={{ fontSize: '0.75rem' }}>
                        {getSeverityLabel(selectedControl.severity)}
                      </span>
                    )}
                  </div>
                  
                  {/* Risk */}
                  {selectedControl?.risk && (
                    <div className="mb-2">
                      <strong>Risco:</strong>{' '}
                      <span className="text-danger" style={{ fontSize: '0.875rem' }}>
                        {selectedControl.risk}
                      </span>
                    </div>
                  )}
                  
                  <div>
                    <strong>Frameworks:</strong>{' '}
                    {selectedControl?.frameworks.map(fw => (
                      <span key={fw} className="badge bg-secondary me-1" style={{ fontSize: '0.75rem' }}>{fw}</span>
                    ))}
                  </div>
                </div>

                {/* Technical Details */}
                {selectedControl?.technicalDetails && (
                  <div className="mb-4 pb-3 border-bottom" style={{ fontSize: '0.875rem' }}>
                    <h6 className="fw-bold mb-2" style={{ fontSize: '0.875rem' }}>Detalhes Técnicos</h6>
                    <div className="text-muted" style={{ fontSize: '0.8rem', lineHeight: '1.6' }}>
                      {selectedControl.technicalDetails}
                    </div>
                  </div>
                )}

                {/* Client Selection */}
                <div className="mb-4">
                  <label className="form-label fw-semibold" style={{ fontSize: '0.875rem' }}>Selecione o Cliente SAP</label>
                  <select
                    className="form-select"
                    value={selectedClient}
                    onChange={e => setSelectedClient(e.target.value)}
                    disabled={executing}
                    style={{ fontSize: '0.875rem' }}
                  >
                    <option value="">-- Selecione um cliente --</option>
                    {sapClients.map(client => (
                      <option key={client._id} value={client._id}>
                        {client.name} ({client.host} - {client.connectionType.toUpperCase()})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Execute Button */}
                {!executionResult && (
                  <div className="text-center mb-4">
                    <button
                      className="btn btn-primary"
                      onClick={executeControl}
                      disabled={executing || !selectedClient}
                      style={{ fontSize: '0.875rem', padding: '0.5rem 1.5rem' }}
                    >
                      {executing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Executando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-play-circle me-2"></i>
                          Executar Controle
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Execution Result */}
                {executionResult && (
                  <div className="mt-4">
                    <div className={`alert ${executionResult.result.passed ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: '0.875rem' }}>
                      <div className="d-flex align-items-center mb-2">
                        <i className={`bi ${executionResult.result.passed ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} fs-4 me-3`}></i>
                        <div>
                          <h6 className="mb-0 fw-bold" style={{ fontSize: '1rem' }}>
                            {executionResult.result.passed ? 'Controle Passou ✓' : 'Controle Falhou ✗'}
                          </h6>
                          <small style={{ fontSize: '0.8rem' }}>
                            Tempo de execução: {executionResult.executionTime}ms
                          </small>
                        </div>
                      </div>
                      {executionResult.result.summary && (
                        <p className="mb-0 mt-2" style={{ fontSize: '0.875rem' }}>{executionResult.result.summary}</p>
                      )}
                    </div>

                    {/* Statistics */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-12">
                        <div className="card border">
                          <div className="card-body text-center py-3">
                            <h3 className="mb-0 text-danger" style={{ fontSize: '1.75rem' }}>{executionResult.result.totalViolations}</h3>
                            <small className="text-muted" style={{ fontSize: '0.8rem' }}>Violações Encontradas</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Findings */}
                    {executionResult.result.findings && executionResult.result.findings.length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-bold mb-3" style={{ fontSize: '0.95rem' }}>
                          Achados ({executionResult.result.findings.length})
                        </h6>
                        
                        <div className="table-responsive">
                          <table className="table table-sm table-hover" style={{ fontSize: '0.85rem' }}>
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: '5%' }}>#</th>
                                <th style={{ width: '10%' }}>Severidade</th>
                                <th style={{ width: '35%' }}>Descrição</th>
                                <th style={{ width: '50%' }}>Detalhes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {executionResult.result.findings
                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                .map((finding, idx) => {
                                  const actualIndex = (currentPage - 1) * itemsPerPage + idx + 1;
                                  return (
                                    <tr key={idx}>
                                      <td className="align-middle">{actualIndex}</td>
                                      <td className="align-middle">
                                        <span className={`badge ${getSeverityBadgeClass(finding.severity)}`} style={{ fontSize: '0.7rem' }}>
                                          {getSeverityLabel(finding.severity)}
                                        </span>
                                      </td>
                                      <td className="align-middle">{finding.description}</td>
                                      <td className="align-middle">
                                        {Object.keys(finding.data).length > 0 && (
                                          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                            {Object.entries(finding.data).map(([key, value]) => (
                                              <div key={key}>
                                                <strong>{key}:</strong> {JSON.stringify(value)}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>

                        {/* Paginação */}
                        {executionResult.result.findings.length > itemsPerPage && (() => {
                          const totalPages = Math.ceil(executionResult.result.findings.length / itemsPerPage);
                          const maxPagesToShow = 5; // Mostrar no máximo 5 botões de página
                          let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                          let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
                          
                          // Ajustar startPage se estivermos próximos ao final
                          if (endPage - startPage < maxPagesToShow - 1) {
                            startPage = Math.max(1, endPage - maxPagesToShow + 1);
                          }
                          
                          const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
                          
                          return (
                            <nav>
                              <ul className="pagination pagination-sm justify-content-center mb-0">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                  >
                                    Anterior
                                  </button>
                                </li>
                                
                                {startPage > 1 && (
                                  <>
                                    <li className="page-item">
                                      <button className="page-link" onClick={() => setCurrentPage(1)}>
                                        1
                                      </button>
                                    </li>
                                    {startPage > 2 && (
                                      <li className="page-item disabled">
                                        <span className="page-link">...</span>
                                      </li>
                                    )}
                                  </>
                                )}
                                
                                {pages.map(page => (
                                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button 
                                      className="page-link" 
                                      onClick={() => setCurrentPage(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                ))}
                                
                                {endPage < totalPages && (
                                  <>
                                    {endPage < totalPages - 1 && (
                                      <li className="page-item disabled">
                                        <span className="page-link">...</span>
                                      </li>
                                    )}
                                    <li className="page-item">
                                      <button className="page-link" onClick={() => setCurrentPage(totalPages)}>
                                        {totalPages}
                                      </button>
                                    </li>
                                  </>
                                )}
                                
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                  >
                                    Próxima
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          );
                        })()}
                      </div>
                    )}

                    {/* Recommendations */}
                    {executionResult.result.recommendations && executionResult.result.recommendations.length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-bold mb-3" style={{ fontSize: '0.95rem' }}>Recomendações</h6>
                        <ul className="list-group">
                          {executionResult.result.recommendations.map((rec, idx) => (
                            <li key={idx} className="list-group-item" style={{ fontSize: '0.85rem' }}>
                              <i className="bi bi-lightbulb text-warning me-2"></i>
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowExecutionModal(false)}
                  disabled={executing}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Toast */}
        {toast.visible && (
          <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 11 }}>
            <div className={`toast show align-items-center text-bg-${toast.variant} border-0`}>
              <div className="d-flex">
                <div className="toast-body">{toast.message}</div>
                <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToast({ visible: false, message: '' })}></button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SapControlsRepository;
